import { useMemo, useState } from "react";

// Conteúdo da Jornada do Cliente (usado pelo modal e pela página cheia).
// Histórico COMPLETO da pessoa (uid canônico + aliases), sem filtro de produto nem período:
// páginas de qualquer produto (inclusive snippet antigo), leads, checkout em 3 passos, vídeos e compras.

const TIPOS = {
    pageview: { icon: "👁️", label: "Página", cls: "border-blue-500" },
    lead: { icon: "📋", label: "Lead", cls: "border-cyan-500" },
    checkout_intent: { icon: "👆", label: "Clicou no checkout", cls: "border-yellow-500" },
    checkout: { icon: "🛒", label: "Foi ao checkout", cls: "border-orange-500" },
    checkout_redirect: { icon: "🛒", label: "Saiu pro checkout", cls: "border-orange-500" },
    click: { icon: "👆", label: "Clique", cls: "border-white/[0.16]" },
    video_progress: { icon: "🎬", label: "Vídeo", cls: "border-indigo-500" },
    venda: { icon: "💰", label: "Compra", cls: "border-emerald-500" },
};
const CUSTOM = { icon: "⚡", cls: "border-white/[0.16]" };

export function UtmBadges({ utms, size = "xs" }) {
    if (!utms) return null;
    const badges = [
        { key: "source", val: utms.source || utms.utm_source, color: "bg-blue-900/40 text-blue-300", label: "source" },
        { key: "medium", val: utms.medium || utms.utm_medium, color: "bg-cyan-900/40 text-cyan-300", label: "medium" },
        { key: "campaign", val: utms.campaign || utms.utm_campaign, color: "bg-amber-900/40 text-amber-300", label: "campaign" },
        { key: "term", val: utms.term || utms.utm_term, color: "bg-yellow-900/40 text-yellow-300", label: "term" },
        { key: "content", val: utms.content || utms.utm_content, color: "bg-orange-900/40 text-orange-300", label: "content" },
    ].filter(b => b.val);
    if (badges.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1">
            {badges.map(b => (
                <span key={b.key} className={`${b.color} px-1.5 py-0.5 rounded ${size === "sm" ? "text-xs" : "text-[10px]"}`}>{b.label}: {b.val}</span>
            ))}
        </div>
    );
}

function formatSeconds(s) {
    if (!s) return "0s";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m${sec}s` : `${sec}s`;
}
function formatR$(v) {
    return "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}
function extractPath(url) {
    try { const u = new URL(url); return u.hostname + u.pathname.replace(/\/$/, ""); } catch { return url; }
}
function diaKey(d) {
    return new Date(d).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function JornadaTimeline({ data, visitorId, compact = false }) {
    const [soMarcos, setSoMarcos] = useState(false);
    const { header, kpis, timeline } = data;

    const itens = useMemo(() => {
        const base = soMarcos ? timeline.filter(t => t.tipo !== "pageview" && t.tipo !== "video_progress") : timeline;
        // agrupa por dia
        const grupos = [];
        for (const t of base) {
            const k = t.data ? diaKey(t.data) : "sem data";
            const g = grupos[grupos.length - 1];
            if (g && g.dia === k) g.itens.push(t); else grupos.push({ dia: k, itens: [t] });
        }
        return grupos;
    }, [timeline, soMarcos]);

    const txt = compact ? "text-xs" : "text-sm";
    const statusCls = header.status === "comprou" ? "bg-emerald-900/50 text-emerald-400" : header.status === "lead" ? "bg-blue-900/50 text-blue-400" : "bg-surface-3 text-ink-2";
    const statusLabel = header.status === "comprou" ? "Comprou" : header.status === "lead" ? "Lead" : "Visitante";

    return (
        <div className={compact ? "space-y-4" : "space-y-6"}>
            {/* Identidade */}
            <div className={`bg-surface-2 rounded-lg ${compact ? "p-4" : "p-6"}`}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div><p className="text-[10px] text-ink-4 uppercase">Nome</p><p className={`${txt} font-medium text-white truncate`}>{header.nome || "—"}</p></div>
                    <div><p className="text-[10px] text-ink-4 uppercase">Email</p><p className={`${txt} text-ink-2 truncate`}>{header.email || "—"}</p></div>
                    <div>
                        <p className="text-[10px] text-ink-4 uppercase">WhatsApp</p>
                        {header.telefone ? <a href={`https://wa.me/${header.telefone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className={`${txt} text-emerald-400 hover:underline`}>{header.telefone}</a> : <p className={`${txt} text-ink-4`}>—</p>}
                    </div>
                    <div><p className="text-[10px] text-ink-4 uppercase">Status</p><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCls}`}>{statusLabel}</span></div>
                </div>

                <div className="mt-3"><UtmBadges utms={header} size={compact ? "xs" : "sm"} /></div>
                {header.utm_campaign && <p className="text-[10px] text-ink-5 mt-1">UTMs do primeiro acesso registrado.</p>}

                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div><p className="text-[10px] text-ink-4 uppercase">Primeiro acesso</p><p className="text-ink-2 text-xs">{header.primeiro_acesso ? new Date(header.primeiro_acesso).toLocaleString("pt-BR") : "—"}</p></div>
                    <div><p className="text-[10px] text-ink-4 uppercase">Último acesso</p><p className="text-ink-2 text-xs">{header.ultimo_acesso ? new Date(header.ultimo_acesso).toLocaleString("pt-BR") : "—"}</p></div>
                    <div><p className="text-[10px] text-ink-4 uppercase">Total comprado</p><p className="text-emerald-400 font-semibold text-xs">{header.valor_compra > 0 ? formatR$(header.valor_compra) : "—"}</p></div>
                    <div>
                        <p className="text-[10px] text-ink-4 uppercase">Visitor ID</p>
                        <p className="text-ink-4 text-[10px] font-mono truncate" title={visitorId}>{header.visitor_id_canonico || visitorId}</p>
                        {header.visitor_ids_merged && <p className="text-[10px] text-ink-5">{header.visitor_ids_merged.length} ids unificados</p>}
                    </div>
                </div>

                {header.produtos_visitados?.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <span className="text-[10px] text-ink-4 uppercase mr-1">Produtos visitados</span>
                        {header.produtos_visitados.map(p => <span key={p.id} className="bg-brand-900/40 text-brand-300 px-2 py-0.5 rounded-full text-[10px]">{p.nome}</span>)}
                    </div>
                )}
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {[
                    { label: "Páginas", value: kpis.pageviews, color: "text-blue-400" },
                    { label: "Leads", value: kpis.leads ?? 0, color: "text-cyan-400" },
                    { label: "Cliques checkout", value: kpis.cliques, color: "text-yellow-400" },
                    { label: "Chegou checkout", value: kpis.chegou_checkout ?? 0, color: "text-orange-400" },
                    { label: "Compras", value: `${kpis.compras}${kpis.orderbumps ? ` +${kpis.orderbumps} bump` : ""}`, color: "text-emerald-400" },
                    { label: "Vídeo", value: formatSeconds(kpis.video_total_seconds), color: "text-indigo-400" },
                ].map(k => (
                    <div key={k.label} className="bg-surface-2 p-2.5 rounded-lg text-center">
                        <p className="text-[10px] text-ink-4">{k.label}</p>
                        <p className={`${compact ? "text-base" : "text-lg"} font-bold ${k.color}`}>{k.value}</p>
                    </div>
                ))}
            </div>
            {kpis.dias_ate_compra != null && (
                <p className="text-xs text-ink-3 -mt-2">Da primeira visita à primeira compra: <strong className="text-ink-2">{kpis.dias_ate_compra === 0 ? "mesmo dia" : `${kpis.dias_ate_compra} dia(s)`}</strong>.</p>
            )}

            {/* Timeline */}
            <div className={`bg-surface-2 rounded-lg ${compact ? "p-4" : "p-6"}`}>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-ink-3 uppercase tracking-wider">Linha do tempo · {timeline.length} evento(s)</h3>
                    <label className="flex items-center gap-1.5 text-xs text-ink-3 cursor-pointer">
                        <input type="checkbox" checked={soMarcos} onChange={e => setSoMarcos(e.target.checked)} className="accent-emerald-500" />
                        Só leads, checkouts e compras
                    </label>
                </div>
                {itens.length === 0 && <p className="text-xs text-ink-4">Nenhum evento registrado.</p>}
                <div className="space-y-5">
                    {itens.map(g => (
                        <div key={g.dia}>
                            <p className="text-[11px] text-ink-4 uppercase tracking-wider mb-2 sticky top-0">{g.dia}</p>
                            <div className="relative">
                                <div className="absolute left-3.5 top-0 bottom-0 w-px bg-surface-3" />
                                <div className="space-y-3">
                                    {g.itens.map((ev, i) => {
                                        const meta = TIPOS[ev.tipo] || { ...CUSTOM, label: ev.tipo.replace(/_/g, " ") };
                                        const isVenda = ev.tipo === "venda";
                                        return (
                                            <div key={i} className="flex gap-3 relative">
                                                <div className={`w-7 h-7 rounded-full bg-surface-2 flex items-center justify-center text-xs z-10 border-2 flex-shrink-0 ${meta.cls}`}>{meta.icon}</div>
                                                <div className={`flex-1 min-w-0 pb-1 ${isVenda ? "bg-emerald-900/15 border border-emerald-700/30 rounded-lg p-3 -mt-1" : ""}`}>
                                                    <div className="flex items-baseline gap-2 flex-wrap">
                                                        <span className={`font-medium text-xs ${isVenda ? "text-emerald-300" : "text-white"}`}>{meta.label}</span>
                                                        <span className="text-[10px] text-ink-4">{ev.data ? new Date(ev.data).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                                                        {ev.produto_nome && !isVenda && <span className="bg-brand-900/30 text-brand-300 px-1.5 py-0.5 rounded text-[10px]">{ev.produto_nome}</span>}
                                                    </div>
                                                    {ev.url && <p className="text-[11px] text-ink-3 truncate" title={ev.url}>{extractPath(ev.url)}</p>}
                                                    {ev.link_url && (ev.tipo === "checkout" || ev.tipo === "checkout_intent" || ev.tipo === "checkout_redirect") && <p className="text-[10px] text-ink-5 truncate" title={ev.link_url}>→ {extractPath(ev.link_url)}</p>}
                                                    {ev.titulo && ev.tipo !== "venda" && ev.tipo !== "pageview" && <p className="text-[11px] text-ink-2">{ev.titulo}</p>}
                                                    {ev.detalhes && ev.tipo === "video_progress" && <p className="text-xs text-indigo-400">{formatSeconds(ev.detalhes.seconds)} / {formatSeconds(ev.detalhes.duration)}</p>}
                                                    {ev.detalhes && ev.tipo === "lead" && <p className="text-xs text-cyan-400">{[ev.detalhes.nome, ev.detalhes.email, ev.detalhes.telefone].filter(Boolean).join(" — ")}</p>}
                                                    {isVenda && ev.detalhes && (
                                                        <div className="mt-1 space-y-0.5">
                                                            <p className="text-sm text-white font-semibold">{ev.titulo} <span className="text-emerald-400">{formatR$(ev.detalhes.valor)}</span></p>
                                                            {ev.detalhes.bumps?.length > 0 && (
                                                                <ul className="text-[11px] text-amber-300">
                                                                    {ev.detalhes.bumps.map((b, j) => <li key={j}>➕ {b.produto} · {formatR$(b.valor)}</li>)}
                                                                </ul>
                                                            )}
                                                            <p className="text-[11px] text-ink-3">
                                                                {ev.detalhes.bumps?.length > 0 && <>Total {formatR$(ev.detalhes.valor_total)} · </>}
                                                                {ev.detalhes.metodo && <>{ev.detalhes.metodo} · </>}
                                                                {ev.detalhes.transacao} · {ev.detalhes.status}
                                                            </p>
                                                            <p className="text-[11px] text-ink-4">{ev.detalhes.paginas_antes ?? 0} página(s) vista(s) antes desta compra</p>
                                                        </div>
                                                    )}
                                                    {ev.utms && <div className="mt-1"><UtmBadges utms={ev.utms} /></div>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
